import { useMutation } from '@apollo/client';
import { useNotify } from '@newfront-insurance/next-notifications';
import { useCallback, useState } from 'react';

import { GENERATE_AI_SUMMARIES, getRootCustomSkillUuid, type PendingSkillAction } from './custom-skill-model';
import type { CustomSkillRecord } from '../../../../queries/chats';
import {
  DELETE_CUSTOM_SKILL,
  PUBLISH_CUSTOM_SKILL_DRAFT,
  SET_SHARED_CUSTOM_SKILL_ENABLED,
  UPDATE_CUSTOM_SKILL,
} from '../../../../queries/mutations';
import { getErrorMessage } from '../../lib/error-message';

export function useCustomSkillActions({
  showSharedSkills,
  refreshAllSkills,
}: {
  showSharedSkills: boolean;
  refreshAllSkills: () => Promise<void>;
}) {
  const notify = useNotify();
  const [pendingAction, setPendingAction] = useState<PendingSkillAction | null>(null);
  const [updateCustomSkill] = useMutation(UPDATE_CUSTOM_SKILL);
  const [setSharedCustomSkillEnabled] = useMutation(SET_SHARED_CUSTOM_SKILL_ENABLED);
  const [deleteCustomSkill] = useMutation(DELETE_CUSTOM_SKILL);
  const [publishCustomSkillDraft] = useMutation(PUBLISH_CUSTOM_SKILL_DRAFT);

  const isSkillPending = useCallback((uuid: string) => pendingAction?.uuid === uuid, [pendingAction]);

  const toggleSkill = useCallback(
    async (skill: CustomSkillRecord) => {
      if (skill.source === 'shared' && !showSharedSkills) {
        return undefined;
      }

      setPendingAction({ type: 'toggle', uuid: skill.uuid });
      try {
        let updatedSkill: CustomSkillRecord | null | undefined;

        if (skill.source === 'shared') {
          const result = await setSharedCustomSkillEnabled({
            variables: {
              uuid: skill.uuid,
              command: { enabled: !skill.enabled },
            },
          });
          updatedSkill = result.data?.setSharedCustomSkillEnabled;
        } else {
          const result = await updateCustomSkill({
            variables: {
              uuid: skill.uuid,
              command: {
                enabled: !skill.enabled,
                generateAiSummaries: GENERATE_AI_SUMMARIES,
              },
            },
          });
          updatedSkill = result.data?.updateCustomSkill;
        }

        await refreshAllSkills();
        notify.success(skill.enabled ? 'Custom skill disabled.' : 'Custom skill enabled.');
        return updatedSkill ?? undefined;
      } catch (toggleError) {
        notify.error(getErrorMessage(toggleError, 'Unable to update custom skill.'));
        return undefined;
      } finally {
        setPendingAction(null);
      }
    },
    [notify, refreshAllSkills, setSharedCustomSkillEnabled, showSharedSkills, updateCustomSkill],
  );

  const removeSkill = useCallback(
    async (skill: CustomSkillRecord) => {
      setPendingAction({ type: 'delete', uuid: skill.uuid });
      try {
        await deleteCustomSkill({ variables: { uuid: skill.uuid } });
        await refreshAllSkills();
        notify.success('Custom skill deleted.');
        return true;
      } catch (deleteError) {
        notify.error(getErrorMessage(deleteError, 'Unable to delete custom skill.'));
        return false;
      } finally {
        setPendingAction(null);
      }
    },
    [deleteCustomSkill, notify, refreshAllSkills],
  );

  const publishDraft = useCallback(
    async (skill: CustomSkillRecord) => {
      setPendingAction({ type: 'publish', uuid: skill.uuid });
      try {
        await publishCustomSkillDraft({ variables: { uuid: getRootCustomSkillUuid(skill) } });
        await refreshAllSkills();
        notify.success('Custom skill update published.');
        return true;
      } catch (publishError) {
        notify.error(getErrorMessage(publishError, 'Unable to publish custom skill update.'));
        return false;
      } finally {
        setPendingAction(null);
      }
    },
    [notify, publishCustomSkillDraft, refreshAllSkills],
  );

  return {
    isSkillPending,
    pendingAction,
    publishDraft,
    removeSkill,
    toggleSkill,
  };
}
